import { useCallback, useEffect, useRef, useState } from "react";

export type ReflectionRecorderStatus = "idle" | "recording" | "error";

export interface UseReflectionRecorderResult {
  status: ReflectionRecorderStatus;
  permissionDenied: boolean;
  elapsedSeconds: number;
  startRecording: () => Promise<{ permissionDenied: boolean }>;
  stopRecording: () => Promise<Blob | null>;
}

const RECORDER_MIME_TYPE_CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/x-m4a",
];

function selectRecorderMimeType() {
  if (typeof MediaRecorder === "undefined" || typeof MediaRecorder.isTypeSupported !== "function") {
    return undefined;
  }

  return RECORDER_MIME_TYPE_CANDIDATES.find((mimeType) => MediaRecorder.isTypeSupported(mimeType));
}

export function useReflectionRecorder(): UseReflectionRecorderResult {
  const [status, setStatus] = useState<ReflectionRecorderStatus>("idle");
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const elapsedIntervalRef = useRef<number | null>(null);
  const recordingStartedAtRef = useRef<number | null>(null);

  const startRecording = useCallback(async (): Promise<{ permissionDenied: boolean }> => {
    setPermissionDenied(false);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      chunksRef.current = [];
      setElapsedSeconds(0);
      recordingStartedAtRef.current = Date.now();
      const recorderMimeType = selectRecorderMimeType();
      const recorder = recorderMimeType
        ? new MediaRecorder(stream, { mimeType: recorderMimeType })
        : new MediaRecorder(stream);
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      elapsedIntervalRef.current = window.setInterval(() => {
        const startedAt = recordingStartedAtRef.current;
        if (startedAt === null) return;
        setElapsedSeconds(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
      }, 250);
      setStatus("recording");
      return { permissionDenied: false };
    } catch (error) {
      setPermissionDenied(true);
      setStatus("error");
      console.error("[ReflectionRecorder] microphone_start_failed", error);
      return { permissionDenied: true };
    }
  }, []);

  const stopRecording = useCallback(async () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder) {
      setStatus("idle");
      return null;
    }

    const stream = mediaStreamRef.current;

    const blob = await new Promise<Blob | null>((resolve) => {
      recorder.onstop = () => {
        const firstChunkType = chunksRef.current.find((chunk) => chunk.type)?.type;
        const mimeType = recorder.mimeType || firstChunkType || "audio/webm";
        resolve(chunksRef.current.length > 0 ? new Blob(chunksRef.current, { type: mimeType }) : null);
      };
      recorder.stop();
      stream?.getTracks().forEach((track) => track.stop());
      mediaRecorderRef.current = null;
      mediaStreamRef.current = null;
      if (elapsedIntervalRef.current !== null) {
        window.clearInterval(elapsedIntervalRef.current);
        elapsedIntervalRef.current = null;
      }
      recordingStartedAtRef.current = null;
    });

    setStatus("idle");
    setElapsedSeconds(0);
    return blob;
  }, []);

  // Clean up microphone stream and timer if the component unmounts mid-recording
  useEffect(() => {
    return () => {
      if (elapsedIntervalRef.current !== null) {
        window.clearInterval(elapsedIntervalRef.current);
        elapsedIntervalRef.current = null;
      }
      mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
      if (mediaRecorderRef.current?.state === "recording") {
        mediaRecorderRef.current.stop();
      }
      mediaRecorderRef.current = null;
      recordingStartedAtRef.current = null;
    };
  }, []);

  return {
    status,
    permissionDenied,
    elapsedSeconds,
    startRecording,
    stopRecording,
  };
}
